<template>
  <v-card>
    <v-card-title v-if="mode === 'edit'" class="headline"
      >Edit Address</v-card-title
    >
    <v-card-title v-else class="headline">Add a New Address</v-card-title>
    <v-card-text>
      <v-form @submit.prevent="onSubmit" ref="form" v-model="valid">
        <v-row>
          <v-col>
            <v-text-field
              id="record.address"
              v-model="record.address"
              label="Street Address 1 *"
              required
              :rules="addressFieldRules"
            ></v-text-field>
            <v-text-field
              id="record.address2"
              v-model="record.address2"
              label="Street Address 2"
            ></v-text-field>
            <v-text-field
              id="record.city"
              v-model="record.city"
              label="City *"
              required
              :rules="cityFieldRules"
            ></v-text-field>
            <v-autocomplete
              id="record.state"
              :filter="stateFilter"
              :items="stateList"
              :loading="loading"
              :rules="stateFieldRules"
              :search-input.sync="search"
              item-color="primary"
              item-text="displayName"
              item-value="abbreviation"
              label="State *"
              v-model="record.state"
              required
            >
              <template v-slot:selection="data">
                <span>{{ data.item.abbreviation }}</span>
              </template>
              <template v-slot:item="data">
                <span>
                  {{ data.item.abbreviation }} -
                  {{ data.item.displayName }}
                </span>
              </template>
            </v-autocomplete>
            <div>
              <v-autocomplete
                id="record.county"
                :disabled="!record.state"
                :items="filteredCountyList"
                :loading="loading"
                :rules="countyFieldRules"
                :search-input.sync="searchCounty"
                item-color="primary"
                item-text="label"
                item-value="optionId"
                label="County/Parish *"
                required
                v-model="record.county"
              >
                <template v-slot:selection="data">
                  <span>{{ data.item.label }} </span>
                </template>
                <template v-slot:item="data">
                  <span>
                    {{ data.item.label }}
                  </span>
                </template>
              </v-autocomplete>
            </div>
            <v-row class="ml-0 mr-1">
              <v-text-field
                id="record.zip"
                v-model="record.zip"
                label="ZIP *"
                v-mask="maskZip"
                required
                :rules="zipFieldRules"
              ></v-text-field>
              <v-btn
                href="https://tools.usps.com/zip-code-lookup.htm?byaddress"
                target="_blank"
                class="ml-4 mt-3"
                >USPS Zip+4 Lookup</v-btn
              >
            </v-row>
            <v-autocomplete
              :items="timezoneList"
              :rules="timezoneFieldRules"
              item-color="primary"
              item-text="name"
              item-value="value"
              label="Timezone *"
              v-model="record.timezone"
              required
            ></v-autocomplete>
            <div class="ml-2 mt-2">* Required field</div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn small outlined @click="cancel" class="mr-1">Cancel</v-btn>
      <v-btn small color="primary" :disabled="!valid" @click="submit"
        >Submit</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import { find, groupBy, keyBy, mapValues } from "lodash";
import { mapActions, mapMutations, mapState } from "vuex";
import { mask } from "vue-the-mask";

export default {
  computed: {
    stateList() {
      const list = this.clarityStateList.map(state => {
        const stateObject = {
          abbreviation: state.abbreviation,
          displayName: state.label,
          optionId: state.optionId,
        };
        return stateObject;
      });
      const filteredList = list
        .filter(state => parseInt(state.optionId, 10) <= 51)
        .sort((a, b) => {
          return a.displayName < b.displayName ? -1 : 1;
        });
      return filteredList;
    },
    filteredCountyList() {
      if (this.selectedStateId) {
        const lookedUpArray = this.stateCountyLookup[this.selectedStateId];
        return lookedUpArray.sort((a, b) => (a.label > b.label ? 1 : -1));
      }
      return [];
    },
    stateCountyLookup() {
      const filteredZipList = this.zipCodeList.filter(record => {
        if (record.county && record.county.toLowerCase() !== "null") {
          return record.county;
        }
      });
      // Group the zip codes by state.
      const zipListGroupedByState = groupBy(
        filteredZipList,
        item => item.state
      );

      // Create a county lookup object where the keys are the county ids.
      const countyLookup = keyBy(this.countyList, "optionId");

      /*
      Return a state lookup object where the state ids are the keys and the values are an an array of county objects.
      mapValues() preserves the keys in the object over which it iterates: https://lodash.com/docs/4.17.15#mapValues
      */
      const stateCountyLookup = mapValues(
        zipListGroupedByState,
        zipCodeListByState => {
          const zipListGroupedByCountyId = groupBy(
            zipCodeListByState,
            item => item.county
          );
          const countyKeys = Object.keys(zipListGroupedByCountyId);
          const countyList = countyKeys.map(countyKey => {
            const county = countyLookup[countyKey];
            return county;
          });
          return countyList;
        }
      );

      return stateCountyLookup;
    },
    selectedStateId() {
      // This computed value is used in filteredCountyList to find the county list in stateCountyLookup.
      if (this.record.state && this.stateList.length) {
        const stateObject = find(this.stateList, state => {
          return state.abbreviation === this.record.state;
        });
        return stateObject.optionId;
      }

      return null;
    },
    ...mapState({
      clarityStateList: state => state.clarityState.list,
      countyList: state => state.clarityCounty.list,
      record: state => state.address.record,
      organizationId: state => state.session.user.organizationId,
      zipCodeList: state => state.clarityZip.list,
    }),
  },
  directives: {
    mask,
  },
  async created() {
    try {
      this.loading = true;
      await Promise.all([
        this.fetchCountyList({ reload: false }),
        this.fetchStateList({ reload: false }),
        this.fetchZipCodeList({ reload: false }),
      ]);
    } catch (e) {
      console.error(e);
    }
    this.loading = false;
  },
  async mounted() {
    if (this.mode === "create") {
      this.$refs.form.reset();
    }
  },
  data() {
    return {
      addressFieldRules: [value => !!value || "Street Address 1 is required."],
      cityFieldRules: [value => !!value || "City is required."],
      countyFieldRules: [value => !!value || "County/Parish is required."],
      loading: false,
      maskZip: "#####-####",
      search: null,
      searchCounty: null,
      stateFieldRules: [value => !!value || "State is required."],
      timezoneList: [
        {
          name: "America/Anchorage",
          value: "America/Anchorage",
        },
        {
          name: "America/Chicago",
          value: "America/Chicago",
        },
        {
          name: "America/Honolulu",
          value: "America/Honolulu",
        },
        {
          name: "America/Los Angeles",
          value: "America/Los_Angeles",
        },
        {
          name: "America/New York",
          value: "America/New_York",
        },
        {
          name: "America/Salt Lake City",
          value: "America/Salt_Lake_City",
        },
      ],
      timezoneFieldRules: [value => !!value || "Timezone is required."],
      valid: false,
      zipFieldRules: [value => !!value || "ZIP is required."],
    };
  },
  methods: {
    cancel() {
      this.setRecord({});
      this.$refs.form.reset();
      this.$emit("setDialog", false);
    },
    async submit() {
      this.record.organizationId = this.organizationId;
      try {
        if (this.mode === "edit") {
          await this.updateRecord([this.addressId, this.record]);
        } else {
          await this.createRecord();
        }

        this.$refs.form.reset();
        this.$emit("setDialog", false);
        this.$emit("setAddressId", this.record.addressId);
        await this.fetchList();
        this.setRecord({});
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
        if (this.mode === "edit") {
          this.notifyErrorAddressUpdate();
        } else {
          this.notifyErrorAddressCreate();
        }
      }
    },
    stateFilter(item, queryText) {
      const displayNameText = item.displayName.toLowerCase();
      const abbreviationText = item.abbreviation.toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        displayNameText.indexOf(searchText) > -1 ||
        abbreviationText.indexOf(searchText) > -1
      );
    },
    ...mapActions({
      createRecord: "address/create",
      fetchList: "address/list",
      fetchCountyList: "clarityCounty/list",
      fetchStateList: "clarityState/list",
      fetchZipCodeList: "clarityZip/list",
      updateRecord: "address/update",
    }),
    ...mapMutations({
      setRecord: "address/setRecord",
    }),
  },
  notifications: {
    notifyErrorAddressUpdate: {
      title: "Error",
      message: "Could not update address.",
      type: "warn",
    },
    notifyErrorAddressCreate: {
      title: "Error",
      message: "Could not create a new address.",
      type: "warn",
    },
  },
  props: ["addressId", "mode"],
};
</script>
